import React, { useEffect, useRef, useState } from 'react';
import * as Accordion from '@radix-ui/react-accordion';
import { ChevronDown, Send, CheckCircle, Mail, Phone, MapPin, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { siteData } from '../data/mock';

export const Contact = () => {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log('Form Submitted:', formData);
    localStorage.setItem('contactForm', JSON.stringify(formData));
    setSubmitted(true);
    setTimeout(() => setSubmitted(false), 3000);
    setFormData({ name: '', email: '', message: '' });
  };

  return (
    <section id="contact" ref={ref} className="py-24 lg:py-32 bg-slate-900 text-white relative overflow-hidden">
      {/* Dynamic Background */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-vibrant-purple/20 rounded-full blur-3xl opacity-30 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-vibrant-blue/20 rounded-full blur-3xl opacity-30" />
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">

          {/* FAQ Column */}
          <div className={`transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-12'}`}>
            <div className="inline-block px-3 py-1 mb-4 text-xs font-semibold tracking-wider text-vibrant-blue uppercase bg-vibrant-blue/10 rounded-full border border-vibrant-blue/20">
              Support
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold font-outfit mb-6">Frequently Asked <span className="text-vibrant-blue">Questions</span></h2>
            <p className="text-slate-400 mb-12 text-lg">
              Have questions? We have answers. If you don't see yours here, drop us a message!
            </p>

            <Accordion.Root type="single" defaultValue="item-1" collapsible className="space-y-4">
              {siteData.faqs.map((faq, index) => (
                <Accordion.Item
                  key={faq.id}
                  value={`item-${index + 1}`}
                  className="overflow-hidden rounded-2xl bg-white/5 border border-white/10"
                >
                  <Accordion.Header>
                    <Accordion.Trigger className="flex w-full items-center justify-between px-6 py-5 text-left text-lg font-medium text-white transition-all hover:bg-white/5 data-[state=open]:bg-white/5 data-[state=open]:text-vibrant-blue group">
                      {faq.question}
                      <ChevronDown className="h-5 w-5 text-slate-400 transition-transform duration-300 ease-[cubic-bezier(0.87,0,0.13,1)] group-data-[state=open]:rotate-180" />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <Accordion.Content className="data-[state=open]:animate-accordion-down data-[state=closed]:animate-accordion-up overflow-hidden">
                    <div className="px-6 pb-6 pt-2 text-slate-300 leading-relaxed">
                      {faq.answer}
                    </div>
                  </Accordion.Content>
                </Accordion.Item>
              ))}
            </Accordion.Root>
          </div>

          {/* Contact Form Column */}
          <div className={`transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="rounded-[2.5rem] p-8 lg:p-12  border-white/10 bg-white/5 backdrop-blur-2xl relative overflow-hidden">
              {/* Decorative Gradient Border Glow */}
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-vibrant-blue via-vibrant-purple to-vibrant-red" />

              <h3 className="text-3xl font-bold font-outfit mb-2">Get in Touch</h3>
              <p className="text-slate-400 mb-8">We'd love to hear about your wedding plans.</p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">Your Name</label>
                  <input
                    type="text"
                    id="name"
                    required
                    className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:border-vibrant-blue focus:ring-1 focus:ring-vibrant-blue transition-all outline-none"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
                  <input
                    type="email"
                    id="email"
                    required
                    className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:border-vibrant-blue focus:ring-1 focus:ring-vibrant-blue transition-all outline-none"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">Message</label>
                  <textarea
                    id="message"
                    required
                    rows={4}
                    className="w-full rounded-xl bg-white/5 border border-white/10 px-4 py-3 text-white placeholder:text-slate-500 focus:border-vibrant-blue focus:ring-1 focus:ring-vibrant-blue transition-all outline-none resize-none"
                    placeholder="Tell us about your dream wedding website..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitted}
                  className={`w-full h-14 rounded-xl text-lg font-semibold transition-all duration-300 ${submitted
                    ? 'bg-green-500 text-white'
                    : 'bg-gradient-to-r from-vibrant-blue to-vibrant-purple hover:opacity-90 text-white shadow-lg shadow-vibrant-blue/25'
                    }`}
                >
                  {submitted ? (
                    <span className="flex items-center justify-center">
                      <CheckCircle className="mr-2 h-5 w-5" /> Message Sent!
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <Send className="mr-2 h-5 w-5" /> Send Message
                    </span>
                  )}
                </Button>
              </form>

              <div className="mt-10 pt-8 border-t border-white/10 grid grid-cols-3 gap-4">
                <a href={`mailto:${siteData.contactEmail}`} className="flex flex-col items-center gap-2 text-slate-400 hover:text-white transition-colors p-4 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-vibrant-purple">
                    <Mail className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium">Email</span>
                </a>
                <a href={`tel:${siteData.whatsappNumber}`} className="flex flex-col items-center gap-2 text-slate-400 hover:text-white transition-colors p-4 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-vibrant-blue">
                    <Phone className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium">Call</span>
                </a>
                <a href={`https://wa.me/${siteData.whatsappNumber}`} target="_blank" rel="noreferrer" className="flex flex-col items-center gap-2 text-slate-400 hover:text-white transition-colors p-4 rounded-xl hover:bg-white/5 border border-transparent hover:border-white/10">
                  <div className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-vibrant-green">
                    <MessageCircle className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium">WhatsApp</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
